<script setup lang="ts">
import { productAvailability } from '@/js/api/forms.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';
import { ERROR_REQUEST_TEXT, showErrorNotify } from '@/js/notification.ts';
import { getPhonePlaceholder } from '@/js/phone-maska.ts';
import { Captcha, RECAPTCHA_FIELD_NAME } from '@/js/recaptcha.ts';
import { classname } from '@/js/utils.ts';
import BaseInputTel from '@/js/vue/base/base-tel.vue';
import { ref, watch } from 'vue';

defineProps<{
  image: string
  name: string
  price: string
  productId: number
}>();

const formRef = ref<HTMLFormElement>();
const captcha = ref<Captcha>();
watch(formRef, (container) => {
  if (container) {
    captcha.value = new Captcha(container);
  }
}, {
  immediate: true,
});
const successTextShown = ref(false);

const loading = ref(false);
const cnButton = classname('base-button');
async function handleSubmit() {
  try {
    loading.value = true;
    captcha.value?.render();
    const token = await captcha.value?.execute();
    const formData = new FormData(formRef.value);
    formData.set(RECAPTCHA_FIELD_NAME, token);
    const response = await productAvailability(formData);
    if (response.data?.success) {
      successTextShown.value = true;
      captcha.value?.reset();
    }
    else {
      showErrorNotify({ text: ERROR_REQUEST_TEXT });
    }
  }
  catch (e) {
    console.error(e);
    showErrorNotify({ text: ERROR_REQUEST_TEXT });
  }
  finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="product-available-modal">
    <div class="product-available-modal__left-column">
      <div class="product-gallery__image-container">
        <img :src="image" alt="" width="262" height="182">
      </div>

      <div class="product-available-modal__name">
        {{ name }}
      </div>
      <div class="product-available-modal__price">
        <span v-if="price">
          <span class="product-available-modal__price-heading">{{ getLocaleValue('page_product_cost') }}: </span>
          {{ price }}
        </span>
        <span v-else>{{ getLocaleValue('product_request_price') }}</span>
      </div>
    </div>
    <form ref="formRef" class="base-form product-available-modal__right-column callback-modal" @submit.prevent="handleSubmit">
      <div v-if="successTextShown">
        {{ getLocaleValue('request_has_been_sent_successfully') }}
      </div>
      <div v-else class="base-form__fields callback-modal__inner">
        <input type="hidden" name="product_id" :value="productId">
        <label class="base-field">
          <input type="text" :placeholder="getLocaleValue('name')" name="name" required>
        </label>
        <BaseInputTel name="phone" required :placeholder="getPhonePlaceholder()" />
        <textarea
          cols="30"
          rows="10"
          :placeholder="getLocaleValue('comment')"
          class="base-textarea"
          name="message"
        />
        <div class="base-form__checkbox-label">
          <label class="base-checkbox">
            <input
              type="checkbox"
              class="base-checkbox__control"
              name="agreement"
              required
              checked
            >
            <span class="base-checkbox__label">
              <span v-html="getLocaleValue('privacy_policy')" />
            </span>
          </label>
        </div>
        <button
          type="submit"
          class="callback-modal__btn"
          :class="cnButton({ type: 'primary', loading })"
        >
          {{ getLocaleValue('send') }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped lang="scss"></style>
